import { useReactiveVar } from '@apollo/client';
import { FieldContext, SubmitHelpers } from '@area2k/use-form';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { Alert, AlertTitle } from '@mui/material';
import { format, subYears } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';

import { getUTCDate, getWeeksRanges } from '../../Payrolls/util';

import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import Icon from '@/elements/Icon';
import Form from '@/form';
import TextField from '@/form/TextField';
import TextSelectField from '@/form/TextSelectField';
import colors from '@/styles/colors';
import { Payroll } from '@/types/graphql';
import { currentTenantVar } from '@/util/apollo/cache';
import { LONG_DATE_FORMAT, MONDAY_OF_WEEK } from '@/util/constants';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type PayrollItem = Payroll;

type Props = {
  hideModal: () => void;
  setPayrollInitiatedMessage: (value: boolean) => void;
  onPayrollCreated?: () => void;
};

type FormValues = {
  weekDay: PayrollItem['periodStart'];
  payday: PayrollItem['payday'];
};

const NewPayrollModalV2 = ({
  hideModal,
  setPayrollInitiatedMessage,
  onPayrollCreated,
}: Props) => {
  const currentTenant = useReactiveVar(currentTenantVar);
  const [weeksOptions, setWeeksOptions] = useState<any[]>([]);
  const [payrollError, setPayrollError] = useState<any>();
  const [isLoading, setIsLoading] = useState(false);

  const tenantId = currentTenant!.id;
  const initialValues: FormValues = {
    weekDay: getUTCDate(MONDAY_OF_WEEK).toISOString(),
    payday: '',
  };

  const [formValues, setFormValues] = useState<FormValues>(initialValues);

  const handleFormValuesChange = <T extends any>(
    fieldContext: FieldContext<T>,
    fieldId: keyof FormValues,
  ) => {
    setFormValues((prevValues) => ({
      ...prevValues,
      [fieldId]: fieldContext.value,
    }));
  };

  const handleSubmit = useCallback(
    async (values: FormValues, { setFormError }: SubmitHelpers) => {
      setIsLoading(true);
      setPayrollError(undefined);
      try {
        // Convert ISO8601 dates to the format expected by the API
        const weekDayDate = values.weekDay ? new Date(values.weekDay) : null;
        const paydayDate = values.payday ? new Date(values.payday) : null;

        const params: any = {
          tenantId: tenantId,
        };

        if (weekDayDate) {
          params.weekDay = format(weekDayDate, 'yyyy-MM-dd');
        }

        if (paydayDate) {
          params.payday = format(paydayDate, 'yyyy-MM-dd');
        }

        await axiosRubyClient.post('/api/payrolls', params);

        hideModal();
        setPayrollInitiatedMessage(true);
        // Call the callback to refresh the payroll list
        if (onPayrollCreated) {
          onPayrollCreated();
        }
      } catch (err: any) {
        console.error('Error creating payroll:', err);
        setIsLoading(false);
        try {
          // Try to parse error response
          const errorResponse = err.response?.data;
          if (errorResponse) {
            setPayrollError({
              message:
                errorResponse.message ||
                errorResponse.error ||
                'Failed to create payroll',
              input_errors:
                errorResponse.errors || errorResponse.input_errors || [],
            });
          } else {
            setPayrollError({
              message: err.message || 'Failed to create payroll',
              input_errors: [],
            });
          }
        } catch (e) {
          setPayrollError({
            message: 'Failed to create payroll. Please try again.',
            input_errors: [],
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [tenantId, hideModal, setPayrollInitiatedMessage, onPayrollCreated],
  );

  useEffect(() => {
    const startDate = subYears(new Date(), 1);
    const endDate = new Date();
    const range = getWeeksRanges(startDate, endDate).map((dates) => {
      return {
        label: `${format(dates.start!, LONG_DATE_FORMAT)} - ${format(
          dates.end!,
          LONG_DATE_FORMAT,
        )}`,
        value: getUTCDate(dates.start).toISOString(),
      };
    });
    setWeeksOptions(range);
  }, []);

  return (
    <Modal
      disableClickout
      size="xs"
      title="Run New Payroll"
      onRequestClose={hideModal}
    >
      <Card.Section>
        {payrollError && (
          <Alert
            icon={<Icon icon={faExclamationTriangle} size={'lg'} />}
            severity="error"
            style={{
              marginBottom: '1rem',
              border: `1px solid ${colors.dangerLightest}`,
            }}
          >
            <AlertTitle>{payrollError?.message}</AlertTitle>
            {payrollError?.input_errors?.length > 0 && (
              <ul>
                {payrollError.input_errors.map((err: any, idx: number) => (
                  <li key={idx}>{err?.message || err}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}

        <Form initialValues={formValues} onSubmit={handleSubmit}>
          {weeksOptions.length > 0 && (
            <TextSelectField
              callback={(fieldContext) => {
                handleFormValuesChange(fieldContext, 'weekDay');
              }}
              fieldId="weekDay"
              label="Select Week"
              options={weeksOptions}
            />
          )}
          <TextField
            required
            callback={(fieldContext) => {
              handleFormValuesChange(fieldContext, 'payday');
            }}
            fieldId="payday"
            label="Payday"
            placeholder="mm/dd/yyyy"
            style={{ textTransform: 'uppercase' }}
            type="date"
          />
          <Stack justify="end">
            <Button
              a11yLabel="Submit form"
              id="submit-payroll-btn"
              isLoading={isLoading}
              label="Run Payroll"
              type="submit"
            />
          </Stack>
        </Form>
      </Card.Section>
    </Modal>
  );
};

export default NewPayrollModalV2;
